import { Check, DeviceTabletSpeaker, LinkBreak, PencilSimple } from '@phosphor-icons/react';
import { Button, Icon, IconButton, Text, TextField } from '@shh/ui';
import { useState } from 'react';
import { friendlyError, renameDevice, unpairDevice } from '../../data/api';
import type { Device, WithId } from '../../data/family';
import { dateFormat } from '../../lib/locale';
import { settingsText } from '../../i18n/settings';

const dateFmt = dateFormat({ dateStyle: 'medium' });
const DAY = 86_400_000;
/** a device in use reports daily – two weeks of silence: wiped, replaced or paired again */
const QUIET_DAYS = 14;

/**
 * The paired shared devices (1.29, Mike 28.10.: "diese kann ich auch gar nicht weiter
 * unterscheiden"): a name per device, when it was last in use, unpairing only after a second tap.
 */
export function DeviceList({ devices, now }: { devices: WithId<Device>[]; now: number }) {
  return (
    <ul className="flex flex-col gap-gap-xs">
      {devices
        .toSorted((a, b) => (b.lastSeenAt ?? b.pairedAt) - (a.lastSeenAt ?? a.pairedAt))
        .map((d) => (
          <DeviceRow key={d.id} device={d} now={now} />
        ))}
    </ul>
  );
}

function DeviceRow({ device: d, now }: { device: WithId<Device>; now: number }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(d.name);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string>();
  const seen = d.lastSeenAt ?? d.pairedAt;
  const quiet = now - seen > QUIET_DAYS * DAY;
  const status = d.lastSeenAt
    ? settingsText.lastSeen(
        now - d.lastSeenAt < DAY ? settingsText.todayWord : dateFmt.format(d.lastSeenAt),
      )
    : settingsText.pairedOn(dateFmt.format(d.pairedAt));

  return (
    <li className="flex flex-col gap-gap-xs rounded-control bg-surface-raised p-inset-xs">
      <div className="flex items-center gap-gap-sm">
        <Icon icon={DeviceTabletSpeaker} size="md" className="text-text-secondary" />
        {editing ? (
          <form
            className="flex flex-1 items-end gap-gap-xs"
            onSubmit={(e) => {
              e.preventDefault();
              setError(undefined);
              void renameDevice(d.id, name).then(
                () => setEditing(false),
                (err: unknown) => setError(friendlyError(err)),
              );
            }}
          >
            <TextField
              label={settingsText.deviceName}
              value={name}
              onChange={setName}
              maxLength={40}
              autoFocus
              className="flex-1"
            />
            <IconButton
              icon={Check}
              type="submit"
              aria-label={settingsText.saveName}
              isDisabled={!name.trim()}
            />
          </form>
        ) : (
          <span className="flex flex-1 flex-col">
            <span className="font-semibold">{d.name}</span>
            <span
              className={quiet ? 'text-xs text-feedback-warning' : 'text-xs text-text-secondary'}
            >
              {quiet ? settingsText.quietSince(dateFmt.format(seen)) : status}
            </span>
          </span>
        )}
        {!editing && (
          <IconButton
            icon={PencilSimple}
            aria-label={settingsText.rename(d.name)}
            onPress={() => setEditing(true)}
          />
        )}
        <IconButton
          icon={LinkBreak}
          aria-label={settingsText.unpair(d.name)}
          onPress={() => setConfirm(true)}
        />
      </div>
      {confirm && (
        <div className="flex flex-wrap items-center gap-gap-sm">
          <Text tone="secondary" className="flex-1">
            {settingsText.unpairHint}
          </Text>
          <Button variant="danger" onPress={() => void unpairDevice(d.id)}>
            {settingsText.unpairConfirm}
          </Button>
          <Button variant="quiet" onPress={() => setConfirm(false)}>
            {settingsText.cancel}
          </Button>
        </div>
      )}
      {error && <p className="text-sm font-semibold text-feedback-danger">{error}</p>}
    </li>
  );
}
